import {
  resolveRemoteLink,
  preserveSyncCursor,
  TASK_REMOTE_LINK_FIELDS,
  type Calendar,
  type Label,
  type RecurrenceOverride,
  type RecurrenceRule,
  type SyncGraph,
  type Task,
  type Topic,
} from "@app/core";
import type { SqlStatement, SqlValue, StorageAdapter } from "../adapter/adapter.js";
import { yieldToTask } from "../util/yield-task.js";
import { CalendarsRepository } from "./calendars.repo.js";
import { TasksRepository } from "./tasks.repo.js";

interface ApplyProgress {
  done: number;
  total: number;
  kind: keyof SyncGraph;
}

type ApplyProgressFn = (progress: ApplyProgress) => void;

const BATCH_SIZE = 500;

/**
 * Which chokepoint semantics a bulk statement carries (see core
 * sync/device-local.ts): every upsert bumps `mut` on conflict and seeds
 * `mut = 1` on insert; "server-apply" statements ADDITIONALLY self-acknowledge
 * `server_acked_mut` in the same statement — the content being written came
 * from the sync server, so it is in step with it by construction, while an
 * interleaved local edit lands its own `mut` bump and stays dirty.
 */
type BulkMode = "local" | "server-apply";

const calendarCols = [
  "id", "name", "color", "hidden", "owner_id", "external_calendar_id", "external_source",
  "external_account", "sync_direction", "sync_token", "last_synced_at",
  "created_at", "updated_at", "deleted_at",
] as const;
const calendarUpdateCols = calendarCols.filter((c) => c !== "id" && c !== "created_at");

const topicCols = ["id", "name", "parent_id", "color", "icon", "owner_id", "created_at", "updated_at", "deleted_at"] as const;
const topicUpdateCols = topicCols.filter((c) => c !== "id" && c !== "created_at");

const labelCols = ["id", "name", "color", "created_at", "updated_at", "deleted_at"] as const;
const labelUpdateCols = labelCols.filter((c) => c !== "id" && c !== "created_at");

const recurrenceCols = ["id", "rrule", "dtstart", "until", "tz", "exdates", "created_at", "updated_at", "deleted_at"] as const;
const recurrenceUpdateCols = recurrenceCols.filter((c) => c !== "id" && c !== "created_at");

const taskCols = [
  "id", "title", "notes", "due_at", "deadline_at", "all_day", "duration_minutes",
  "priority", "matrix_pos", "matrix_hidden", "custom_color", "is_event", "location", "label_ids", "reminder_offsets",
  "tz", "topic_ids", "calendar_id", "external_id", "ical_uid", "etag", "rev", "source",
  "external_calendar_id", "link_rev", "status", "completed_at", "snoozed_until", "recurrence_id",
  "parent_id", "subtask_parent_id", "subtask_order", "created_at", "updated_at", "deleted_at",
] as const;
const REMOTE_LINK_COLS = new Set<string>([...TASK_REMOTE_LINK_FIELDS, "link_rev"]);
/**
 * The general upsert leaves the provider remote-link columns ALONE on conflict:
 * `bulkUpsert` backs restore / undo / ICS import / duplicate, none of which know
 * the current link, and writing their stale copy of it would unlink a live
 * Google event (→ a duplicate event on the next push). A brand-new row still
 * gets whatever link the INSERT carries.
 */
const taskUpdateCols = taskCols.filter(
  (c) => c !== "id" && c !== "created_at" && !REMOTE_LINK_COLS.has(c),
);
/** Sync apply DOES write the link — but only after {@link resolveRemoteLink} has
 *  resolved it grow-only against the row already in storage. */
const taskSyncUpdateCols = taskCols.filter((c) => c !== "id" && c !== "created_at");

const overrideCols = [
  "id", "recurrence_id", "recurrence_id_ref", "range", "status", "snoozed_until",
  "due_at", "duration_minutes", "title", "notes", "priority", "custom_color", "all_day",
  "rrule", "created_at", "updated_at", "deleted_at",
] as const;
const overrideUpdateCols = overrideCols.filter((c) => c !== "id" && c !== "created_at");

export function canApplySyncGraphBulk(db: StorageAdapter): boolean {
  return typeof db.executeBatch === "function";
}

/** Heterogeneous row sets for {@link bulkUpsert}, in dependency order. */
export interface BulkUpsertRows {
  calendars?: Calendar[];
  topics?: Topic[];
  labels?: Label[];
  recurrenceRules?: RecurrenceRule[];
  tasks?: Task[];
  recurrenceOverrides?: RecurrenceOverride[];
}

/** One table's rows plus the statement builder for them. Kept as a lazy slicer
 *  so a chunk's statements are built only when that chunk is about to run —
 *  building all of them first is its own long task on a 20k-row import. */
interface BulkGroup {
  count: number;
  slice(from: number, to: number): SqlStatement[];
}

function group<T>(rows: readonly T[] | undefined, to: (row: T) => SqlStatement): BulkGroup {
  const list = rows ?? [];
  return { count: list.length, slice: (from, until) => list.slice(from, until).map(to) };
}

export interface BulkUpsertOptions {
  /** Called after each batch lands, for a progress indicator. */
  onProgress?: ((done: number, total: number) => void) | undefined;
  /**
   * Hand the main thread back between batches. Required for anything a user
   * waits on in the BROWSER: `MemoryAdapter.execute` resolves synchronously, so
   * awaiting it stays inside the same task and a whole import is one freeze.
   * Native adapters (desktop/Android SQLite) yield on their own IPC anyway.
   */
  yieldBetweenBatches?: boolean;
}

/**
 * Upsert whole rows across several tables in dependency order. Uses chunked
 * native batches where the adapter supports them (desktop/Android — one IPC
 * round-trip per {@link BATCH_SIZE} rows instead of one per row); otherwise
 * falls back to per-statement execution (browser MemoryAdapter, which honours
 * the same `ON CONFLICT(id) DO UPDATE` upsert incl. its SET clause). Rows are
 * written verbatim — callers own `updated_at` stamping and any skip-unchanged
 * logic — except the device-local `mut` counters, which the statements own
 * (insert seeds mut=1 dirty-for-both; conflict bumps `mut=mut+1`), so a restore
 * or undo is a local change both sync engines will pick up.
 */
export async function bulkUpsert(db: StorageAdapter, rows: BulkUpsertRows, opts?: BulkUpsertOptions): Promise<void> {
  const groups = [
    group(rows.calendars, (c: Calendar) => calendarStatement(c, "local")),
    group(rows.topics, (t: Topic) => topicStatement(t, "local")),
    group(rows.labels, (l: Label) => labelStatement(l, "local")),
    group(rows.recurrenceRules, (r: RecurrenceRule) => recurrenceStatement(r, "local")),
    group(rows.tasks, (t: Task) => taskStatement(t, "local")),
    group(rows.recurrenceOverrides, (o: RecurrenceOverride) => overrideStatement(o, "local")),
  ];
  const total = groups.reduce((n, g) => n + g.count, 0);
  if (total === 0) return;
  let done = 0;
  for (const g of groups) {
    for (let i = 0; i < g.count; i += BATCH_SIZE) {
      const chunk = g.slice(i, Math.min(i + BATCH_SIZE, g.count));
      if (db.executeBatch) await db.executeBatch(chunk);
      else for (const s of chunk) await db.execute(s.sql, s.params ?? []);
      done += chunk.length;
      opts?.onProgress?.(done, total);
      if (opts?.yieldBetweenBatches && done < total) await yieldToTask();
    }
  }
}

export async function applySyncGraphBulk(
  db: StorageAdapter,
  g: SyncGraph,
  onProgress?: ApplyProgressFn,
  beforeWrite?: () => void,
): Promise<void> {
  if (!db.executeBatch) throw new Error("Storage adapter does not support batch execution");
  const progress = new ProgressCounter(graphCount(g), onProgress);

  beforeWrite?.();
  const existingCalendars = new Map((await new CalendarsRepository(db).listAll()).map((c) => [c.id, c] as const));
  beforeWrite?.();
  const calendars = g.calendars.map((c) => preserveSyncCursor(c, existingCalendars.get(c.id) ?? null));

  // Resolve each incoming task's remote link grow-only against what is already
  // stored, so a peer's (or an echo's) empty link can never unlink a live event
  // while a peer that HAS the link can still teach it to this device.
  beforeWrite?.();
  const existingTasks = g.tasks.length > 0
    ? new Map((await new TasksRepository(db).listAll()).map((t) => [t.id, t] as const))
    : new Map<string, Task>();
  beforeWrite?.();
  const tasks = g.tasks.map((t) => resolveRemoteLink(t, existingTasks.get(t.id) ?? null));

  await executeKind(db, "calendars", calendars.map((c) => calendarStatement(c, "server-apply")), progress, beforeWrite);
  await executeKind(db, "topics", g.topics.map((t) => topicStatement(t, "server-apply")), progress, beforeWrite);
  await executeKind(db, "labels", g.labels.map((l) => labelStatement(l, "server-apply")), progress, beforeWrite);
  await executeKind(db, "recurrenceRules", g.recurrenceRules.map((r) => recurrenceStatement(r, "server-apply")), progress, beforeWrite);
  await executeKind(db, "tasks", tasks.map((t) => taskSyncStatement(t)), progress, beforeWrite);
  await executeKind(db, "recurrenceOverrides", g.recurrenceOverrides.map((o) => overrideStatement(o, "server-apply")), progress, beforeWrite);
}

async function executeKind(
  db: StorageAdapter,
  kind: keyof SyncGraph,
  statements: SqlStatement[],
  progress: ProgressCounter,
  beforeWrite?: () => void,
): Promise<void> {
  if (!db.executeBatch) throw new Error("Storage adapter does not support batch execution");
  for (let i = 0; i < statements.length; i += BATCH_SIZE) {
    const chunk = statements.slice(i, i + BATCH_SIZE);
    beforeWrite?.();
    await db.executeBatch(chunk);
    beforeWrite?.();
    progress.add(kind, chunk.length);
  }
}

/** `hasGoogle` = whether the table carries a `google_acked_mut` column
 *  (labels/topics do not — they never reach Google). */
function upsertSql(
  cols: readonly string[],
  updateCols: readonly string[],
  table: string,
  mode: BulkMode,
  hasGoogle: boolean,
): string {
  const mutCols = hasGoogle ? "mut,server_acked_mut,google_acked_mut" : "mut,server_acked_mut";
  const insertCols = `${cols.join(",")},${mutCols}`;
  const placeholders = [...cols.map(() => "?"), ...mutCols.split(",").map(() => "?")].join(",");
  const sets = [
    ...updateCols.map((c) => `${c}=excluded.${c}`),
    "mut=mut+1",
    ...(mode === "server-apply" ? ["server_acked_mut=mut+1"] : []),
  ];
  return `INSERT INTO ${table} (${insertCols}) VALUES (${placeholders}) ON CONFLICT(id) DO UPDATE SET ${sets.join(",")}`;
}

function statement(
  table: string,
  cols: readonly string[],
  updateCols: readonly string[],
  params: SqlValue[],
  mode: BulkMode,
  hasGoogle = true,
): SqlStatement {
  const mutParams: SqlValue[] = hasGoogle
    ? [1, mode === "server-apply" ? 1 : 0, 0]
    : [1, mode === "server-apply" ? 1 : 0];
  return { sql: upsertSql(cols, updateCols, table, mode, hasGoogle), params: [...params, ...mutParams] };
}

function calendarStatement(c: Calendar, mode: BulkMode): SqlStatement {
  return statement("calendars", calendarCols, calendarUpdateCols, [
    c.id, c.name, c.color ?? null, c.hidden ? 1 : 0, c.owner_id ?? null,
    c.external_calendar_id ?? null, c.external_source ?? null, c.external_account ?? null,
    c.sync_direction ?? "off", c.sync_token ?? null, c.last_synced_at ?? null,
    c.created_at, c.updated_at, c.deleted_at,
  ], mode);
}

function topicStatement(t: Topic, mode: BulkMode): SqlStatement {
  return statement("topics", topicCols, topicUpdateCols, [
    t.id, t.name, t.parent_id ?? null, t.color ?? null, t.icon ?? null, t.owner_id ?? null,
    t.created_at, t.updated_at, t.deleted_at,
  ], mode, false);
}

function labelStatement(l: Label, mode: BulkMode): SqlStatement {
  return statement("labels", labelCols, labelUpdateCols, [
    l.id, l.name, l.color ?? null, l.created_at, l.updated_at, l.deleted_at,
  ], mode, false);
}

function recurrenceStatement(r: RecurrenceRule, mode: BulkMode): SqlStatement {
  return statement("recurrence_rules", recurrenceCols, recurrenceUpdateCols, [
    r.id, r.rrule, r.dtstart, r.until, r.tz ?? null, JSON.stringify(r.exdates ?? []),
    r.created_at, r.updated_at, r.deleted_at,
  ], mode);
}

function taskStatement(t: Task, mode: BulkMode): SqlStatement {
  return taskStatementWith(t, taskUpdateCols, mode);
}

function taskSyncStatement(t: Task): SqlStatement {
  return taskStatementWith(t, taskSyncUpdateCols, "server-apply");
}

function taskStatementWith(t: Task, updateCols: readonly string[], mode: BulkMode): SqlStatement {
  return statement("tasks", taskCols, updateCols, [
    t.id, t.title, t.notes, t.due_at, t.deadline_at, t.all_day ? 1 : 0, t.duration_minutes,
    t.priority, t.matrix_pos ? JSON.stringify(t.matrix_pos) : null, t.matrix_hidden ? 1 : 0,
    t.custom_color ?? null, t.is_event ? 1 : 0, t.location ?? "",
    JSON.stringify(t.label_ids ?? []), JSON.stringify(t.reminder_offsets ?? []), t.tz ?? null,
    JSON.stringify(t.topic_ids ?? []), t.calendar_id ?? null, t.external_id ?? null,
    t.ical_uid ?? null, t.etag ?? null, t.rev ?? 0, t.source ?? null,
    t.external_calendar_id ?? null, t.link_rev ?? 0,
    t.status, t.completed_at ?? null, t.snoozed_until ?? null,
    t.recurrence_id, t.parent_id, t.subtask_parent_id ?? null, t.subtask_order ?? 0,
    t.created_at, t.updated_at, t.deleted_at,
  ], mode);
}

function overrideStatement(o: RecurrenceOverride, mode: BulkMode): SqlStatement {
  return statement("recurrence_overrides", overrideCols, overrideUpdateCols, [
    o.id, o.recurrence_id, o.recurrence_id_ref, o.range, o.status, o.snoozed_until,
    o.due_at, o.duration_minutes, o.title, o.notes, o.priority, o.custom_color,
    o.all_day === null ? null : o.all_day ? 1 : 0, o.rrule,
    o.created_at, o.updated_at, o.deleted_at,
  ], mode);
}

function graphCount(g: SyncGraph): number {
  return (
    g.tasks.length +
    g.recurrenceRules.length +
    g.recurrenceOverrides.length +
    g.labels.length +
    g.topics.length +
    g.calendars.length
  );
}

class ProgressCounter {
  private done = 0;

  constructor(
    private readonly total: number,
    private readonly onProgress?: ApplyProgressFn,
  ) {}

  add(kind: keyof SyncGraph, count: number): void {
    this.done += count;
    if (count > 0) this.onProgress?.({ done: this.done, total: this.total, kind });
  }
}
